import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { useSorting } from '@/scopes/search/search/domain/hooks'
import { SortingExplanation } from '@/scopes/search/search/pages/SearchPage/components/Results/SortingExplanation'
import { mqMin, space, tok } from '@sunwebgroup/ui-components'
import { Button, Icon, Menu, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
`

const Label = styled(Typography)`
  display: none;
  color: ${tok('semantics_content_secondary_color')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    display: flex;
  }
`

const MenuContent = styled(Menu.Content)`
  max-width: 280px;
`

const MenuTrigger = styled(Menu.Trigger)<{ $disabled: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;

  ${Icon} {
    margin-left: unset
  }

  ${({ $disabled }) => $disabled && `pointer-events: none;`}
`

const SortByLabelWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_4')};
`

const SORT_OPTIONS: Record<string, { translation: string, subtext?: string }> = {
  Popularity: {
    translation: 'sort_option_popularity',
    subtext: 'sort_option_popularity_subtext',
  },
  Price: {
    translation: 'sort_option_price',
  },
  CustomersRating: {
    translation: 'sort_option_customer_rating',
  },
  DepartureDate: {
    translation: 'sort_option_departure_date',
  },
}

export function Sorting() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, sorting, setSortingBy } = useSorting()
  const [open, setOpen] = React.useState(false)

  function toggle() {
    setOpen(!open)
  }

  const handleChange = (value: string) => {
    setSortingBy(value)
    toggle()
    sendEvent({
      event: 'sortingSearch',
      description: 'sort product listing',
      element: 'dropdown',
      type: 'sorting on',
      result: value,
      interaction: true,
    })
  }

  return (
    <Menu.Root open={open} onOpenChange={toggle}>
      <Wrapper>
        <MenuTrigger $disabled={isFetching}>
          <SortByLabelWrapper>
            <Label $variant="body-m-bold-tight">{intl.formatMessage({ id: 'sort_label' })}</Label>
            <Button $type="transparentNeutral">
              {intl.formatMessage({ id: `${SORT_OPTIONS[sorting.by].translation}` })}
              <Icon glyph={open ? 'ChevronUpExtraSmall' : 'ChevronDownExtraSmall'} />
            </Button>
          </SortByLabelWrapper>
        </MenuTrigger>
        <SortingExplanation />
      </Wrapper>
      <MenuContent position="bottom-left">
        {Object.entries(SORT_OPTIONS).map(([key, value], index) => (
          <Menu.Item
            glyph={key === sorting.by ? 'CheckMark' : undefined}
            key={key}
            supportingText={value.subtext ? intl.formatMessage({ id: value.subtext }) : undefined}
            onClick={() => handleChange(key)}
            id={`sorting_option_${index + 1}`}
          >
            {intl.formatMessage({ id: value.translation })}
          </Menu.Item>
        ))}
      </MenuContent>
    </Menu.Root>
  )
}
